import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServiceRouter } from '@pershing/pershing-core';
import { OptionTradingServiceDefinition } from './../../models/option/option-trading-service-definition';
import { TransactionType, OrderType, DeskRouteCode, OrderDuration, AccountType, Account, DeskRouteTimeZone, UserType, OptionStrategy } from '../../models/common/trading-common.model';
import { MultiLegOptionTradingOrder, OptionTradingOrder } from '../../models/option/trading-option.model';
import { TradingEntitlements } from './../../models/common/trading-entitlements';

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    })
};

const acctNumParam = 'urn:id:headers.x-AccountNumber';
const ordDltParam = 'urn:id:headers.x-OrdDtlKey';

@Injectable()
export abstract class OptionTradingServiceStrategy {

    private baseUrl: string;
    private platform: string;

    acctNumParam = 'urn:id:headers.x-AccountNumber';

    constructor(protected http: HttpClient, protected serviceRouter: ServiceRouter, @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings?.platform) {
            this.platform = appSettings.platform;
        } else {
            this.platform = 'professional';
        }
        this.baseUrl = this.serviceRouter.getServiceRouteURL(OptionTradingServiceDefinition.getInstance(this.platform), { ignoreOptions: { serviceVersion: true } });
    }

    private getHttpHeadersWithAcctNum(acctNum: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum
        });
    }

    private getHeadersWithAcctNumAndOrdDltKey(acctNum: string,ordDtlKey: string) {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-AccountNumber': acctNum,
            'x-OrdDtlKey': ordDtlKey
        });
    }

    public abstract support(platform: string): boolean;

    public getBaseUrl(): string {
        return this.baseUrl;
    }

    public abstract getOptionStrategies(account: Account, entitlements: TradingEntitlements): string[];

    public abstract getOptionMultiLegTransactionTypes(account: Account, entitlements: TradingEntitlements, strategy: OptionStrategy, isAPA?: boolean): TransactionType[];

    public abstract getTransactionTypes(account: Account, entitlements: TradingEntitlements, isAPA: boolean): TransactionType[];

    public abstract getOrderTypes(isNonDollar: boolean, qty: number): OrderType[];

    public abstract getOptionMultiLegOrderTypes(strategy: OptionStrategy, transactionType: TransactionType, modifier?: any): OrderType[];

    public abstract getDurations(isMultiLegOrder: boolean, orderType?: string, ordQty?: number, userType?: UserType): OrderDuration[];

    public abstract accountTypes(account: Account, transactionType: TransactionType, symAccountType: AccountType): AccountType[];

    public abstract getDeskRouteTimeZone(): DeskRouteTimeZone[];

    public abstract getDeskCode(): DeskRouteCode[];

    public abstract getCodeValuesExchangeList(): Observable<any>;

    public reviewOptionOrder(optionTradingOrder: OptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/review', optionTradingOrder, httpOptions);
    }

    public reviewMultiLegOptionOrder(multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/review-multileg', multiLegOptionTradingOrder, httpOptions);
    }

    public submitOptionOrder(optionTradingOrder: OptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/submit', optionTradingOrder, httpOptions);
    }

    public submitMultiLegOptionOrder(multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/submit-multileg', multiLegOptionTradingOrder, httpOptions);
    }

    public correctOptionOrder(optionTradingOrder: OptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/correct-order', optionTradingOrder, httpOptions);
    }

    public correctMultiLegOptionOrder(multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/correct-multileg', multiLegOptionTradingOrder, httpOptions);
    }

    public getMultiLegCommission(multiLegOptionTradingOrder: MultiLegOptionTradingOrder): Observable<any> {
        return this.http.post(this.getBaseUrl() + '/commission-multileg', multiLegOptionTradingOrder, httpOptions);
    }

    public getMultiLegOrderDetails(acctNum: string, ordDtlKey: string, subRequestTypeCode: string, ordProdCodeTex: string, enterpriseID: string): Observable<any> {
        let encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
        let url = this.getBaseUrl() + `/${acctNumParam}/order/${subRequestTypeCode}/${ordProdCodeTex}/${enterpriseID}/detail-multileg/${ordDltParam}`;
        url = url + '?includeTrailers=false';
        return this.http.get(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public getShortSaleRebate(account: string, symId: string): Observable<any> {
        return this.http.get(this.getBaseUrl() + '/' + account + '/' + symId + '/indicative-rate', httpOptions);
    }

    public getExpiryDate(symId: string): Observable<any> {
        return this.http.get(this.getBaseUrl() + '/' + symId + '/expiry-dates', httpOptions);
    }

    public getStrikePrice(symId: string, optExpDate: string): Observable<any> {
        return this.http.get(this.getBaseUrl() + '/' + symId + '/strike-prices?optExpDate=' + optExpDate, httpOptions);
    }

    public cancelOptionOrder(acctNum: string, ordDtlKey: string, subRequestTypeCode: string): Observable<any> {
      const encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
      const url = this.getBaseUrl() + `/${this.acctNumParam}/order/${subRequestTypeCode}/Option/cancel/${ordDltParam}`;
      return this.http.delete(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public cancelComplexOptionOrder(acctNum: string, ordDtlKey: string, subRequestTypeCode: string, enterpriseID: string): Observable<any> {
      const encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
      const url = this.getBaseUrl() + `/${this.acctNumParam}/order/${subRequestTypeCode}/Option/${enterpriseID}/cancel-multileg/${ordDltParam}`;
      return this.http.delete(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public cancelMultiLegOptionOrder(acctNum: string, ordDtlKey: string, multiLegOrderId: string, subRequestTypeCode: string): Observable<any> {
      const encodedOrdDtlKey = encodeURIComponent(ordDtlKey);
      const url = this.getBaseUrl() + `/${this.acctNumParam}/order/${subRequestTypeCode}/Option/multi-leg-cancel/${ordDltParam}?primOrderID=${multiLegOrderId}`;
      return this.http.delete(url, { headers: this.getHeadersWithAcctNumAndOrdDltKey(acctNum, encodedOrdDtlKey) });
    }

    public getOptionChainQuotes(symId: string, optExpDate: string, putCallInd: string): Observable<any> {
        let url = this.getBaseUrl() + '/' + symId + '/' + putCallInd + '/option-chain-quotes?optExpDate=' + optExpDate;
        return this.http.get(url, httpOptions);
    }

    protected getNonMarginAccountOptionStrategies(entitlements: TradingEntitlements): string[] {
        // Professional users always have access to all strategies as long as they are multi-leg enabled.
        // Because the strategy is only used in the front-end to drive UI display, we're not going to
        // use an enumeration reference in the pushed values. Simple string values will suffice.
        let optionStrategies: string[] = [];
        optionStrategies.push('SINGLE');

        if (entitlements.isBuyWriteStrategyEnabled) {
            optionStrategies.push('BUY_WRITE');
        }

        if (entitlements.isMarriedPutStrategyEnabled) {
            optionStrategies.push('MARRIED_PUT');
        }

        optionStrategies.push('ROLL');

        return optionStrategies;
    }

    protected getMarginAccountOptionStrategies(entitlements: TradingEntitlements): string[] {
        // Professional users always have access to all strategies as long as they are multi-leg enabled.
        // Because the strategy is only used in the front-end to drive UI display, we're not going to
        // use an enumeration reference in the pushed values. Simple string values will suffice.
        let optionStrategies: string[] = [];

        optionStrategies.push('SINGLE');
        if (entitlements.isBuyWriteStrategyEnabled) {
            optionStrategies.push('BUY_WRITE');
        }

        optionStrategies.push('CALENDAR_SPREAD');
        if (entitlements.isCollarStrategyEnabled) {
            optionStrategies.push('COLLAR');
        }

        optionStrategies.push('DIAGONAL_SPREAD');
        if (entitlements.isMarriedPutStrategyEnabled) {
            optionStrategies.push('MARRIED_PUT');
        }

        if (entitlements.isRatioStrategyEnabled && entitlements.isEntitledToComplexRatioOption) {
            optionStrategies.push('RATIO');
        }

        optionStrategies.push('ROLL');
        if (entitlements.isStraddleStrategyEnabled) {
            optionStrategies.push('STRADDLE');
        }

        if (entitlements.isStrangleStrategyEnabled) {
            optionStrategies.push('STRANGLE');
        }

        optionStrategies.push('VERTICAL_SPREAD');

        return optionStrategies;
    }
}
